import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const buildForm = (editing) => ({
  code: editing?.code || '',
  name: editing?.name || '',
  description: editing?.description || '',
  category: editing?.category || '',
  unitPrice: editing?.unitPrice ?? 0,
  unit: editing?.unit || 'unite',
  active: editing?.active ?? true,
});

function ArticleEditorDialog({ open, editing, onClose, onSaved, api }) {
  const [form, setForm] = useState(buildForm(editing));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(buildForm(editing));
      setError('');
    }
  }, [editing, open]);

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        unitPrice: Number(form.unitPrice || 0),
      };

      if (editing?._id) {
        await api.put(`/api/contrat/articles/${editing._id}`, payload);
      } else {
        await api.post('/api/contrat/articles', payload);
      }

      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing?._id ? 'Modifier l article' : 'Nouvel article'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Code" value={form.code} onChange={(e) => setForm((c) => ({ ...c, code: e.target.value }))} />
          <TextField label="Nom" value={form.name} onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((c) => ({ ...c, description: e.target.value }))}
            multiline
            minRows={3}
          />
          <TextField
            label="Categorie"
            value={form.category}
            onChange={(e) => setForm((c) => ({ ...c, category: e.target.value }))}
          />
          <TextField
            label="Prix unitaire"
            type="number"
            value={form.unitPrice}
            onChange={(e) => setForm((c) => ({ ...c, unitPrice: e.target.value }))}
          />
          <TextField label="Unite" value={form.unit} onChange={(e) => setForm((c) => ({ ...c, unit: e.target.value }))} />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={form.active}
              onChange={(e) => setForm((c) => ({ ...c, active: e.target.checked }))}
            />
            <Typography>{form.active ? 'Actif' : 'Inactif'}</Typography>
          </Stack>
          {error ? <Typography color="error">{error}</Typography> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ArticleCatalogManager({ open = false, onClose, api, onChanged, inline = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/contrat/articles/all');
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (inline || open) loadRows();
  }, [inline, loadRows, open]);

  const columns = useMemo(
    () => [
      { accessorKey: 'code', header: 'Code' },
      { accessorKey: 'name', header: 'Nom' },
      { accessorKey: 'category', header: 'Categorie' },
      {
        accessorKey: 'unitPrice',
        header: 'Prix',
        Cell: ({ row }) => `${Number(row.original.unitPrice || 0).toFixed(2)} €`,
      },
      { accessorKey: 'unit', header: 'Unite' },
      {
        accessorKey: 'active',
        header: 'Etat',
        Cell: ({ row }) => (row.original.active ? 'Actif' : 'Inactif'),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Modifier">
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(row.original);
                  setEditorOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton
                size="small"
                color="error"
                onClick={async () => {
                  if (!window.confirm(`Supprimer l article "${row.original.name}" ?`)) return;
                  await api.delete(`/api/contrat/articles/${row.original._id}`);
                  await loadRows();
                  onChanged?.();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [api, loadRows, onChanged]
  );

  const table = useMaterialReactTable({
    localization: MRT_Localization_FR,
    enableGlobalFilter: true,
    enableColumnFilters: true,

    columns,
    data: rows,
    state: { showGlobalFilter: true, showColumnFilters: true,  isLoading: loading },
    enablePagination: false,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
 density: 'compact' },
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  const content = (
    <Box sx={{ width: inline ? '100%' : { xs: '100vw', md: 900 }, p: inline ? 0 : 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant={inline ? 'subtitle1' : 'h6'}>Catalogue articles</Typography>
        <Button
          variant="contained"
          size={inline ? 'small' : 'medium'}
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          Ajouter
        </Button>
      </Stack>

      <MaterialReactTable table={table} />

      <ArticleEditorDialog
        open={editorOpen}
        editing={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={async () => {
          setEditorOpen(false);
          await loadRows();
          onChanged?.();
        }}
        api={api}
      />
    </Box>
  );

  if (inline) return content;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      {content}
    </Drawer>
  );
}
